// Text processing pipelines - importing functions from different files

/**
 * 🔄 Available Pipelines Summary:
 * 
 * == HTML Processing Pipelines ==
 * 1. pipelineTextAsHtmlForMishna - Process Mishna text
 * 2. pipelineTextAsHtmlForTanakh - Process Tanakh text
 * 
 * == Text Cleaning Pipelines ==
 * 3. pipelineCleanTextForMishna - Clean Mishna text
 * 4. pipelineCleanTextForTanakh - Clean Tanakh text
 * 
 * == Array Processing Functions ==
 * 5. removeEmptyHtmlElements - Remove empty HTML elements from array
 */

// פונקציות מקובץ text-cleaning.utils.ts
import {
  removeParentheses,
  unwrapBigTags,
  attachPunctuationToWord,
  splitMaqqaf,
  removeSamekh,
  removePe,
  removeSpanAndP,
  removeSmallTags,
  removeOtherTagsExceptBr,
  removeOnlySquareBrackets,
  removePaseq,
  removeCantillation,
  keepOnlyHebrewAndSpaces,
  normalizeSpaces,
  attachSingleCharacterToPreviousWord,
  removeX24SpanAndAddSpaceBeforePunctuation,
  fixPunctuationSpacing,
  removePunctuation,
  removeHtmlEntities,
  cleanX25SpanSpaces,
  removeEmptyHtmlElements,
  removeXmlLangFromSpans, // ייבוא הפונקציה החדשה
  removeNoteMarkerSpans, // ייבוא הפונקציה להסרת סימני הערות שוליים
  removeUnwantedCharacters, // ייבוא הפונקציה להסרת תווים לא רצויים
  replaceKamatsKatan, // ייבוא הפונקציה להחלפת קמץ קטן
  replaceHolamHaser // ייבוא הפונקציה להחלפת חולם חסר
} from './text-cleaning.utils';

// פונקציות מקובץ text-processing.utils.ts
import {
  movePunctuationInsideSpan,
  removePunctuationSpacesInsideSpan
} from './text-processing.utils';


/**
 * ⚠️ HTML Processing Pipeline for Mishna
 * Processes Mishna text with HTML tags (has <p> tags, no <br> tags)
 * 
 * @warning DO NOT change the order without thorough testing!
 */
export const pipelineTextAsHtmlForMishna = [
  removeNoteMarkerSpans,                   // 0. הסרת סימני הערות שוליים (NoteMarker) לפני כל שאר העיבוד
  removeXmlLangFromSpans,                  // 1. הסרת xml:lang מה-span
  replaceKamatsKatan,                      // 2. החלפת קמץ קטן בקמץ רגיל
  replaceHolamHaser,                       // 3. החלפת חולם חסר בחולם רגיל
  removeUnwantedCharacters,                // 4. הסרת תווים לא רצויים (כולל |)
  removeX24SpanAndAddSpaceBeforePunctuation, // 5. טיפול בתגיות x-24 עם רווח
  removeParentheses,                     // 6. הסרת סוגריים
  splitMaqqaf,                           // 7. פיצול מקפים
  cleanX25SpanSpaces,                    // 8. ניקוי רווחים בתגיות x-25
  attachPunctuationToWord,               // 9. צימוד פיסוק
  // movePunctuationInsideSpan,             // 10. הזזת פיסוק לתוך span
  // removePunctuationSpacesInsideSpan,     // 11. הסרת רווחים בspan
] as const;

/**
 * ⚠️ HTML Processing Pipeline for Tanakh
 * Processes Tanakh text with HTML tags (no <p> tags, has <br> tags and cantillation marks)
 *
 * Kamatz katan (U+05C7) and holam haser for vav (U+05BA) are left as the text
 * writes them. This pipeline used to replace both with the plain kamatz and
 * holam before drawing, as a workaround for fonts that had no glyph for
 * them. The Tanakh screen renders in Ezra SIL, which draws both, so the
 * workaround only ever hid the distinction: 1512 lines of Torah carry a
 * kamatz katan and none of them has been visible on screen. A child reading
 * כׇּל as "kal" has to be able to see that it is "kol".
 *
 * Only the display pipeline changed. The clean pipeline below still replaces
 * both, so a reading is scored exactly as before. When scoring becomes
 * phonetic it will want the distinction too, and that is the moment to take
 * the replacement out of the clean pipeline as well.
 *
 * The Mishna display pipeline still replaces both with the plain kamatz and
 * holam, so Mishna does not show kamatz katan. That is on purpose: Mishna renders
 * in oprFrank, which has no glyph for either, and the Mishna text does not
 * carry U+05C7 anyway.
 *
 * @warning DO NOT change the order without thorough testing!
 */
export const pipelineTextAsHtmlForTanakh = [
  removeParentheses,                     // 1. הסרת סוגריים
  removeUnwantedCharacters,              // 2. הסרת תווים לא רצויים (כולל |)
  unwrapBigTags,                         // 3. פתיחת תגיות big
  removeSmallTags,                       // 4. הסרת תגיות small
  splitMaqqaf,                           // 5. פיצול מקפים
  removeOnlySquareBrackets,              // 6. הסרת סוגריים מרובעים
  removePaseq,                           // 7. הסרת פסק (אם נשאר)
  movePunctuationInsideSpan,             // 8. הזזת פיסוק לתוך span
  removePunctuationSpacesInsideSpan,     // 9. הסרת רווחים בspan
  removeCantillation,                    // 10. הסרת טעמים אחרי פיצול
  removeSamekh,                          // 11. הסרת {ס}
  removePe,                              // 12. הסרת {פ}
  removeHtmlEntities,                    // 13. הסרת ישויות HTML
] as const;

/**
 * ⚠️ Text Cleaning Pipeline for Mishna
 * Cleans Mishna text from HTML tags and handles punctuation spacing
 * 
 * @warning DO NOT change the order without thorough testing!
 */
export const pipelineCleanTextForMishna = [
  replaceKamatsKatan,                  // 1. החלפת קמץ קטן בקמץ רגיל
  replaceHolamHaser,                   // 2. החלפת חולם חסר בחולם רגיל
  removeSpanAndP,                      // 3. הסרת תגיות span ו-p שנשארו
  removeUnwantedCharacters,            // 4. הסרת תווים לא רצויים (כולל |)
  removeSmallTags,                     // 5. הסרת תגיות small
  unwrapBigTags,                       // 6. פתיחת תגיות big 
  removeOtherTagsExceptBr,             // 7. הסרת כל התגיות חוץ מbr
  removePunctuation,                   // 8. הסרת סימני פיסוק
  keepOnlyHebrewAndSpaces,             // 9. רק אותיות עבריות ורווחים
  normalizeSpaces,                     // 10. נירמול רווחים
  fixPunctuationSpacing,               // 11. תיקון רווחי פיסוק
  attachSingleCharacterToPreviousWord, // 12. צימוד אותיות בודדות (חייב להיות אחרון!)
] as const;

/**
 * ⚠️ Text Cleaning Pipeline for Tanakh
 * Cleans Tanakh text from HTML tags and handles cantillation marks
 * 
 * @warning DO NOT change the order without thorough testing!
 */
export const pipelineCleanTextForTanakh = [
  replaceKamatsKatan,                  // 1. החלפת קמץ קטן בקמץ רגיל
  replaceHolamHaser,                   // 2. החלפת חולם חסר בחולם רגיל
  removeSpanAndP,                      // 3. הסרת תגיות span ו-p שנשארו
  removeUnwantedCharacters,            // 4. הסרת תווים לא רצויים (כולל |)
  removeSmallTags,                     // 5. הסרת תגיות small
  unwrapBigTags,                       // 6. פתיחת תגיות big 
  removeOtherTagsExceptBr,             // 7. הסרת כל התגיות חוץ מbr
  removeOnlySquareBrackets,            // 8. הסרת סוגריים מרובעים
  removePaseq,                         // 9. הסרת פסק (אם נשאר)
  removeCantillation,                  // 10. הסרת טעמים (חשוב מאוד לתנ״ך)
  removePunctuation,                   // 11. הסרת סימני פיסוק
  keepOnlyHebrewAndSpaces,             // 12. רק אותיות עבריות ורווחים
  normalizeSpaces,                     // 13. נירמול רווחים
  fixPunctuationSpacing,               // 14. תיקון רווחי פיסוק
  attachSingleCharacterToPreviousWord, // 15. צימוד אותיות בודדות (חייב להיות אחרון!)
] as const;

// ======================================
// === פונקציות עזר לטיפול במערכים ===
// ======================================

/**
 * יצירת פייפליין שלם לעיבוד מערכי טקסט של משניות
 * כולל הסרת איברים ריקים ועיבוד כל איבר בפייפליין
 * 
 * @param textArray מערך של מחרוזות HTML (כמו שמגיע מהשרת)
 * @returns מערך מעובד ללא איברים ריקים ועם עיבוד HTML מלא
 * 
 * @example
 * ```typescript
 * const rawText = [
 *   "<p class=\"x-22 para-style-override-1\"> אַרְבָּעָה אֲבוֹת נְזִיקִים:</p>",
 *   "<p class=\"x-22\"></p>", // איבר ריק שיוסר
 *   "<p class=\"x-22\">וּכְשֶׁהִזִּיק —</p>" // הסימן — יישמר
 * ];
 * 
 * // שלב 1: הסרת איברים ריקים
 * const cleanArray = removeEmptyHtmlElements(rawText);
 * 
 * // שלב 2: עיבוד כל איבר בפייפליין
 * const processedArray = cleanArray.map(line => 
 *   pipelineTextAsHtmlForMishna.reduce((text, fn) => fn(text), line)
 * );
 * ```
 */
export { removeEmptyHtmlElements };


